import { ColumnConfig, CurrentTabRow, FlattenedFeedbackResponse } from '../../utils/customerSentimentutils';
import { CustomerRequestResponse, SolutionStoryListItem } from '../../services/api';
import { extractProjectAccountBU, getLatestFeedbackResponse, getEmailStatus, getCustomerResponseStatus, getZohoStatus, getEscalationResolutionStatusLabel } from '../../utils/customerSentimentutils';

/** Resolve account display value from request (account_name, account, or accounts). */
function getAccountDisplay(req: CustomerRequestResponse): string | null {
  if (req.account_name) return req.account_name;
  if (req.account) return req.account;
  if (req.accounts != null) return Array.isArray(req.accounts) ? req.accounts.filter(Boolean).join(', ') : String(req.accounts);
  return null;
}

export const COLUMN_CONFIGS = {
  feedbackResponses: [
    { id: 'requestNumber', label: 'REQUEST ID', field: (r: FlattenedFeedbackResponse) => r.requestNumber ?? null },
    { id: 'date', label: 'DATE', field: (r: FlattenedFeedbackResponse) => r.date, isDate: true },
    { id: 'projectCode', label: 'PROJECT CODE', field: (r: FlattenedFeedbackResponse) => r.projectCode },
    { id: 'projectName', label: 'PROJECT NAME', field: (r: FlattenedFeedbackResponse) => r.projectName },
    { id: 'account', label: 'ACCOUNT', field: (r: FlattenedFeedbackResponse) => r.account },
    { id: 'projectOwner', label: 'PROJECT OWNER', field: (r: FlattenedFeedbackResponse) => r.projectOwner },
    { id: 'customerName', label: 'CUSTOMER NAME', field: (r: FlattenedFeedbackResponse) => r.customerName },
    { id: 'customerEmail', label: 'CUSTOMER EMAIL', field: (r: FlattenedFeedbackResponse) => r.customerEmail },
    { id: 'region', label: 'REGION', field: (r: FlattenedFeedbackResponse) => r.region },
    { id: 'overallSatisfaction', label: 'OVERALL SATISFACTION', field: (r: FlattenedFeedbackResponse) => r.overallSatisfaction },
    { id: 'deliveryCompletion', label: 'DELIVERY COMPLETION', field: (r: FlattenedFeedbackResponse) => r.deliveryCompletion },
    { id: 'deliveryTimeline', label: 'DELIVERY TIMELINE', field: (r: FlattenedFeedbackResponse) => r.deliveryTimeline },
    { id: 'deliveryClosure', label: 'DELIVERY CLOSURE & HANDOVER', field: (r: FlattenedFeedbackResponse) => r.deliveryClosure },
    { id: 'teamTechnical', label: 'TEAM TECHNICAL EXPERTISE', field: (r: FlattenedFeedbackResponse) => r.teamTechnical },
    { id: 'teamSupport', label: 'TEAM SUPPORT PROVIDED', field: (r: FlattenedFeedbackResponse) => r.teamSupport },
    { id: 'teamResponsiveness', label: 'TEAM RESPONSIVENESS', field: (r: FlattenedFeedbackResponse) => r.teamResponsiveness },
    { id: 'specificContributions', label: 'SPECIFIC CONTRIBUTIONS', field: (r: FlattenedFeedbackResponse) => r.specificContributions },
    { id: 'areasForImprovement', label: 'AREAS FOR IMPROVEMENT', field: (r: FlattenedFeedbackResponse) => r.areasForImprovement },
    { id: 'zohoStatus', label: 'ZOHO STATUS', field: (r: FlattenedFeedbackResponse) => r.zohoStatus },
    {
      id: 'zohoIssueLink',
      label: 'ZOHO LINK',
      field: (r: FlattenedFeedbackResponse) => r.zohoIssueLink ?? null,
    },
  ] as ColumnConfig[],
  feedback: [
    { id: 'request_number', label: 'REQUEST ID', field: (r) => (r as CustomerRequestResponse).request_number },
    { id: 'project_code', label: 'PROJECT CODE', field: (r) => (r as CustomerRequestResponse).project_code || null },
    { id: 'project_name', label: 'PROJECT NAME', field: (r) => (r as CustomerRequestResponse).project_name || null },
    { id: 'account', label: 'ACCOUNT', field: (r) => {
      const req = r as CustomerRequestResponse;
      return req.account_name || getLatestFeedbackResponse(req)?.account_name || null;
    }},
    { id: 'project_account_bu', label: 'REGION', field: (r) => extractProjectAccountBU((r as CustomerRequestResponse).project_account_bu) },
    { id: 'closure_date', label: 'CLOSURE DATE', field: (r) => (r as CustomerRequestResponse).closure_date || null, isDate: true },
    { id: 'date', label: 'DATE RAISED', field: (r) => (r as CustomerRequestResponse).created_at, isDate: true },
    { id: 'email_status', label: 'EMAIL STATUS', field: (r) => getEmailStatus(r as CustomerRequestResponse) },
    { id: 'customer_response_status', label: 'FEEDBACK STATUS', field: (r) => getCustomerResponseStatus(r as CustomerRequestResponse) },
    { id: 'zoho_status', label: 'ZOHO ISSUE', field: (r) => getZohoStatus(r as CustomerRequestResponse) },
  ] as ColumnConfig[],
  escalation: [
    { id: 'request_number', label: 'REQUEST ID', field: (r) => (r as CustomerRequestResponse).request_number },
    { id: 'project_code', label: 'PROJECT CODE', field: (r) => (r as CustomerRequestResponse).project_code || null },
    { id: 'project_name', label: 'PROJECT NAME', field: (r) => (r as CustomerRequestResponse).project_name || null },
    { id: 'account', label: 'ACCOUNT', field: (r) => getAccountDisplay(r as CustomerRequestResponse) },
    { id: 'date_raised', label: 'DATE RAISED', field: (r) => {
      const req = r as CustomerRequestResponse;
      return req.escalation_detail?.date_raised || req.created_at;
    }, isDate: true },
    { id: 'escalated_by', label: 'ESCALATED BY', field: (r) => {
      const req = r as CustomerRequestResponse;
      return req.escalation_detail?.escalated_by_name || req.submitter_name || null;
    }},
    { id: 'level', label: 'LEVEL', field: (r) => {
      const req = r as CustomerRequestResponse;
      return req.escalation_detail?.level === 'individual' ? 'Individual' : 'Project';
    }},
    { id: 'zoho_status', label: 'ZOHO ISSUE', field: (r) => getZohoStatus(r as CustomerRequestResponse) },
    { id: 'status', label: 'STATUS', field: (r) => getEscalationResolutionStatusLabel(r as CustomerRequestResponse) },
  ] as ColumnConfig[],
  appreciation: [
    { id: 'request_number', label: 'REQUEST ID', field: (r) => (r as CustomerRequestResponse).request_number },
    { id: 'project_code', label: 'PROJECT CODE', field: (r) => (r as CustomerRequestResponse).project_code || null },
    { id: 'project_name', label: 'PROJECT NAME', field: (r) => (r as CustomerRequestResponse).project_name || null },
    { id: 'account', label: 'ACCOUNT', field: (r) => getAccountDisplay(r as CustomerRequestResponse) },
    { id: 'date', label: 'DATE', field: (r) => (r as CustomerRequestResponse).created_at, isDate: true },
    { id: 'customer_spoc', label: 'CUSTOMER SPOC', field: (r) => {
      const req = r as CustomerRequestResponse;
      return req.appreciation_detail?.customer_spoc_name || req.submitter_name || null;
    }},
    { id: 'type', label: 'TYPE', field: (r) => {
      const req = r as CustomerRequestResponse;
      return req.appreciation_detail?.level === 'individual' ? 'Individual' : 'Project';
    }},
    { id: 'status', label: 'STATUS', field: () => 'Acknowledged' },
  ] as ColumnConfig[],
  myProjects: [
    { id: 'project_code', label: 'PR CODE', field: (r: { project_code?: string | null }) => r.project_code || null },
    { id: 'account', label: 'ACCOUNT NAME', field: (r: { account?: string | null }) => r.account ?? null },
    { id: 'project_name', label: 'PROJECT NAME', field: (r: { project_name?: string | null }) => r.project_name || null },
    { id: 'project_account_bu', label: 'REGION', field: (r: { project_account_bu?: string | null }) => extractProjectAccountBU(r.project_account_bu) },
    { id: 'closure_date', label: 'CLOSURE DATE', field: (r: { closure_date?: string | null }) => r.closure_date || null, isDate: true },
    { id: 'status', label: 'PROJECT STATUS', field: (r: { status?: string | null }) => r.status || null },
    { id: 'feedback_status', label: 'SIGNOFF STATUS', field: (r: { feedback_status?: string }) => r.feedback_status || null },
    { id: 'solution_story_status', label: 'SOLUTION STORY STATUS', field: (r: { solution_story_status?: string }) => r.solution_story_status || null },
    {
      id: 'request_status',
      label: 'ACTION',
      field: (r: { can_create_request?: boolean }) =>
        r.can_create_request ? 'Create Request' : 'Complete',
    },
  ] as ColumnConfig[],
  solutionStory: [
    { id: 'request_number', label: 'STORY ID', field: (r) => {
      const req = r as CustomerRequestResponse;
      // Return request_number if available, otherwise return null (blank)
      return req.request_number || null;
    }},
    { id: 'status', label: 'STATUS', field: (r) => {
      const row = r as SolutionStoryListItem;
      return row.status ?? null;
    }},
    {
      id: 'submitted_at',
      label: 'SUBMITTED AT',
      field: (r) => (r as SolutionStoryListItem).submitted_at ?? null,
      isDate: true,
    },
    {
      id: 'submitter_email',
      label: 'SUBMITTED BY',
      field: (r) => (r as SolutionStoryListItem).submitter_email ?? null,
    },
    { id: 'id', label: 'STORY ID LINK', field: (r) => {
      const req = r as SolutionStoryListItem;
      if (!req.id) return null;
      const st = req.status;
      if (st === 'Draft' || st === 'Failed' || st === 'Submitting') {
        return `/customer-feedback?type=solution-story&draftId=${req.id}`;
      }
      return `/customer-sentiment?requestId=${req.id}&type=solution-story`;
    }},
    { id: 'project_code', label: 'PR CODE', field: (r) => (r as CustomerRequestResponse).project_code || null },
    {
      id: 'project_name',
      label: 'PROJECT NAME',
      field: (r) => (r as SolutionStoryListItem).project_name ?? null,
    },
    { id: 'account_name', label: 'ACCOUNT NAME', field: (r) => (r as CustomerRequestResponse).account_name || null },
    { id: 'project_owner', label: 'PROJECT OWNER', field: (r) => (r as CustomerRequestResponse).project_owner || null },
    { id: 'opportunity', label: 'OPPORTUNITY', field: (r) => (r as CustomerRequestResponse).opportunity || null },
    { id: 'solution_story_link', label: 'LINK TO STORY', field: (r) => (r as CustomerRequestResponse & { solution_story_link?: string }).solution_story_link || null },
    {
      id: 'email_status',
      label: 'EMAIL STATUS',
      field: (r) => (r as SolutionStoryListItem).email_status ?? null,
    },
  ] as ColumnConfig[],
  publicTestimonial: [
    { id: 'request_number', label: 'TESTIMONIAL ID', field: (r) => {
      const req = r as CustomerRequestResponse;
      return req.request_number || null;
    }},
    { id: 'project_code', label: 'PR CODE', field: (r) => (r as CustomerRequestResponse).project_code || null },
    { id: 'account_name', label: 'ACCOUNT NAME', field: (r) => (r as CustomerRequestResponse).account_name || null },
    { id: 'customer_poc_name', label: 'CUSTOMER POC NAME', field: (r) => {
      const req = r as CustomerRequestResponse;
      return (req as any).customer_poc_name || null;
    }},
    { id: 'customer_poc_designation', label: 'CUSTOMER POC DESIGNATION', field: (r) => {
      const req = r as CustomerRequestResponse;
      return (req as any).customer_poc_designation || null;
    }},
    { id: 'date_testimony_received', label: 'DATE RECEIVED', field: (r) => {
      const req = r as CustomerRequestResponse;
      return (req as any).date_testimony_received || req.created_at || null;
    }, isDate: true },
    { id: 'public_testimonial_link', label: 'LINK', field: (r) => {
      const req = r as CustomerRequestResponse;
      return (req as any).public_testimonial_link || null;
    }},
    { id: 'file_url', label: 'FILE', field: (r) => {
      const req = r as CustomerRequestResponse;
      return (req as any).file_url || null;
    }},
    { id: 'submitter_name', label: 'SUBMITTER', field: (r) => (r as CustomerRequestResponse).submitter_name || null },
    { id: 'created_at', label: 'CREATED AT', field: (r) => (r as CustomerRequestResponse).created_at || null, isDate: true },
  ] as ColumnConfig[],
};
